"use client";

import React, { useEffect, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Menu, UserPlus, X } from "lucide-react";

import { cn } from "@/lib/utils";
import { useLocalePath, useLocale } from "@/lib/useLocale";
import { locales, type Locale } from "@/lib/i18n";
import { useDictionary } from "@/lib/useDictionary";

type NavLink = {
  href: string;
  label: string;
  children?: Array<{ href: string; label: string }>;
};

const MAX_DROPDOWN_ITEMS_PER_COLUMN = 8;

function chunkItems<T>(items: T[], size: number): T[][] {
  const chunks: T[][] = [];

  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }

  return chunks;
}

const localeLabels: Record<Locale, string> = {
  tw: "中文",
  en: "EN",
};

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [expandedMobile, setExpandedMobile] = useState<string | null>(null);

  const locale = useLocale();
  const lp = useLocalePath();
  const pathname = usePathname();
  const dict = useDictionary();

  const navLinks: NavLink[] = [
    { href: "/plans", label: dict.navbar.plans },
    { href: "/templates", label: dict.navbar.templates },
    {
      href: "/tutorial",
      label: dict.navbar.tutorials,
      children: [
        { href: "/mcp", label: "MCP" },
        { href: "/tutorial", label: "Data Studio" },
        { href: "/gsheet-tutorial", label: "Google Sheet" },
        { href: "/extension-tutorial", label: "Google Chrome" },
      ],
    },
    {
      href: "/meta-ads",
      label: dict.navbar.connectors,
      children: [
        { href: "/meta-ads", label: "Meta Ads" },
        { href: "/iginsights", label: "Instagram" },
        { href: "/threads", label: "Threads" },
        { href: "/lap", label: "LINE LAP" },
        { href: "/meta-page", label: dict.navbar.connectorMetaPage },
        { href: "/gads", label: "GAds" },
        { href: "/gsc", label: "GSC" },
        { href: "/ga", label: "GA4" },
        { href: "/cyberbiz", label: "CYBERBIZ" },
        { href: "/shopify", label: "SHOPIFY" },
        // { href: "/shopline", label: "SHOPLINE" },
        { href: "/1shop", label: "1SHOP" },
        { href: "/ruten", label: "露天市集" },
        { href: "/microsoft", label: "Microsoft Ads" },
        { href: "/gbp", label: "Google 商家" },
        { href: "/open-ai", label: "OpenAI Ads" },
      ],
    },
    { href: "/about", label: dict.navbar.about },
    { href: "/articles", label: dict.navbar.articles },
  ];

  const switchLocalePath = (target: Locale) => {
    const rest = pathname.replace(/^\/(tw|en)/, "") || "/";
    return `/${target}${rest}`;
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
        isScrolled
          ? "glass shadow-lg border-b border-white/20"
          : "bg-transparent",
      )}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <Link href={lp("/")} className="flex items-center gap-2 group">
            <Image
              src="/icon/logo.png"
              alt={dict.navbar.logoText}
              width={28}
              height={28}
              className="w-7 h-7 object-contain"
              priority
            />
            <span className="text-xl font-bold text-[var(--blue-800)]">
              {dict.navbar.logoText}
            </span>
          </Link>
          <div className="flex items-center gap-1 mr-2">
            {locales.map((l) => (
              <Link
                key={l}
                href={switchLocalePath(l)}
                className={cn(
                  "px-2 py-1 text-xs font-medium rounded-md transition-colors",
                  l === locale
                    ? "bg-[var(--blue-700)] text-white"
                    : "text-gray-600 hover:bg-gray-100",
                )}
              >
                {localeLabels[l]}
              </Link>
            ))}
          </div>

          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => {
              const childColumns = link.children
                ? chunkItems(link.children, MAX_DROPDOWN_ITEMS_PER_COLUMN)
                : [];

              return (
                <div
                  key={link.href}
                  className={cn(
                    "relative group",
                    link.children?.length
                      ? "after:content-[''] after:absolute after:left-0 after:right-0 after:top-full after:h-3 after:bg-transparent"
                      : "",
                  )}
                >
                  <Link
                    href={lp(link.href)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-[var(--blue-700)] rounded-lg hover:bg-[var(--darker-blue)]/50 transition-all duration-200 inline-flex items-center"
                    aria-haspopup={link.children ? "menu" : undefined}
                  >
                    {link.label}
                    {link.children && (
                      <ChevronDown
                        className="w-4 h-4 ml-1 text-gray-500 group-hover:text-[var(--blue-700)] transition-colors"
                        aria-hidden="true"
                      />
                    )}
                  </Link>

                  {link.children && (
                    <div
                      className="absolute left-0 top-full mt-1 rounded-xl glass shadow-lg border border-white/20 overflow-hidden opacity-0 translate-y-1 pointer-events-none group-hover:opacity-100 group-hover:translate-y-0 group-hover:pointer-events-auto group-focus-within:opacity-100 group-focus-within:translate-y-0 group-focus-within:pointer-events-auto transition-all duration-200"
                      role="menu"
                    >
                      <div className="flex">
                        {childColumns.map((column, columnIndex) => (
                          <div
                            key={`${link.href}-column-${columnIndex}`}
                            className={cn(
                              "min-w-45 py-2",
                              columnIndex > 0 && "border-xl border-white/10",
                            )}
                          >
                            {column.map((child) => (
                              <Link
                                key={child.href}
                                href={lp(child.href)}
                                className="block px-4 py-2 text-sm text-gray-700 hover:text-[var(--blue-700)] hover:bg-[var(--darker-blue)]/50 transition-colors whitespace-nowrap"
                                role="menuitem"
                              >
                                {child.label}
                              </Link>
                            ))}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="hidden lg:flex items-center gap-2">
            <Link
              href="https://admin.quickdash.com.tw/"
                className="hero-cta inline-flex items-center gap-3 rounded-full px-6 py-2 font-bold text-[#6b4318] transition-transform duration-300 hover:scale-[1.04] sm:px-8 sm:py-4 "
            >
              <UserPlus className="w-4 h-4" aria-hidden="true" />
              {dict.navbar.signUp}
            </Link>
          </div>

          <button
            onClick={() => {
              setIsOpen(!isOpen);
              setExpandedMobile(null);
            }}
            className="lg:hidden p-2 rounded-lg hover:bg-[var(--darker-blue)]/50 transition-colors"
            aria-label="Toggle menu"
          >
            {isOpen ? (
              <X className="w-6 h-6 text-gray-700" />
            ) : (
              <Menu className="w-6 h-6 text-gray-700" />
            )}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="lg:hidden glass border-t border-white/20"
          >
            <div className="max-w-7xl mx-auto px-4 py-4 space-y-1">
              {navLinks.map((link, index) => (
                <motion.div
                  key={link.href}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  {link.children?.length ? (
                    <>
                      <button
                        type="button"
                        onClick={() =>
                          setExpandedMobile(
                            expandedMobile === link.href ? null : link.href,
                          )
                        }
                        aria-expanded={expandedMobile === link.href}
                        className="w-full flex items-center justify-between px-4 py-3 text-gray-700 hover:text-[var(--blue-700)] hover:bg-[var(--darker-blue)]/50 rounded-lg transition-all"
                      >
                        {link.label}
                        <ChevronDown
                          className={cn(
                            "w-4 h-4 text-gray-500 transition-transform duration-200",
                            expandedMobile === link.href && "rotate-180",
                          )}
                          aria-hidden="true"
                        />
                      </button>
                      <AnimatePresence initial={false}>
                        {expandedMobile === link.href && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.2 }}
                            className="overflow-hidden"
                          >
                            <div className="flex pl-4">
                              {chunkItems(
                                link.children,
                                MAX_DROPDOWN_ITEMS_PER_COLUMN,
                              ).map((column, columnIndex) => (
                                <div
                                  key={`${link.href}-mobile-column-${columnIndex}`}
                                  className="flex-1 min-w-0"
                                >
                                  {column.map((child) => (
                                    <Link
                                      key={child.href}
                                      href={lp(child.href)}
                                      onClick={() => setIsOpen(false)}
                                      className="block px-4 py-2.5 text-sm text-gray-600 hover:text-[var(--blue-700)] hover:bg-[var(--darker-blue)]/50 rounded-lg transition-all truncate"
                                    >
                                      {child.label}
                                    </Link>
                                  ))}
                                </div>
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </>
                  ) : (
                    <Link
                      href={lp(link.href)}
                      onClick={() => setIsOpen(false)}
                      className="block px-4 py-3 text-gray-700 hover:text-[var(--blue-700)] hover:bg-[var(--darker-blue)]/50 rounded-lg transition-all"
                    >
                      {link.label}
                    </Link>
                  )}
                </motion.div>
              ))}

              <div className="pt-4 border-t border-gray-200 space-y-2">
                <Link
                  href="https://admin.quickdash.com.tw/"
                  className="hero-cta block px-4 py-3 text-center font-semibold text-gray-900 gradient-accent rounded-full font-semibold"
                >
                  <span className="inline-flex items-center justify-center gap-2 text-gray-900">
                    <UserPlus className="w-4 h-4" aria-hidden="true" />
                    {dict.navbar.signUp}
                  </span>
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
